import {observer_mgl} from "mobx-graphlink";
import {PromptPool} from "../../../../../Server/DB/engineConfigs/@EngineConfig/@EC_Visualization.js";
import React, {useRef, useState} from "react";
import {Button, Text} from "react-vcomponents";
import {SelectAudioFileFromPath_ForWebAudioPlayer, WebAudioPlayer} from "../../../../../Utils/Media/WebAudioPlayer.js";
import {ApplyVolumeEffectsToVolumeValue, PlaySoundOpts, SaveAudioDataToFile} from "../../../../../Utils/Bridge/Bridge_Native/MediaPlayer.js";
import {SoundPanningType} from "../../../../../Server/DB/@Shared/EffectPointer.js";
import {GetFilePathForBakedSpatialAudio} from "../../../../../Utils/Services/FilePaths.js";
import {InAndroid} from "../../../../../Utils/Bridge/Bridge_Native.js";
import {ErrToStr} from "../../../../../Utils/General/Errors.js";

export const SpatialAudioBakeUI = observer_mgl((props: {pool: PromptPool, bakeDuration: number, volumeOverride: number|null})=>{
	const {pool, bakeDuration, volumeOverride} = props;

	const [baking, setBaking] = useState(false);
	const [statusMessage, setStatusMessage] = useState<string>("");
	const webAudioPlayerRef = useRef<WebAudioPlayer|null>(null);

	const StartBaking = async()=>{
		setBaking(true);
		setStatusMessage("Initializing...");
		try {
			const soundFilePath = pool.cs_effect?.soundFile?.filePath ?? "";
			const selectedAudioFile = SelectAudioFileFromPath_ForWebAudioPlayer(soundFilePath);
			if (!selectedAudioFile) {
				setStatusMessage(`Could not find audio file matching subpath: ${soundFilePath}`);
				setBaking(false);
				return;
			}

			const finalVolume = volumeOverride != null && volumeOverride > 0
				? volumeOverride
				: ApplyVolumeEffectsToVolumeValue(pool.promptVolume);

			// todo: probably rework this to piggyback more heavily on the existing EffectPointerPlayer code
			// (perhaps using an EffectPointerPlayer actually, but just added some special extra arguments)
			// (and in the process, can end up making the "bake" system more flexible, eg. letting user bake with "simple" panning)
			const player = new WebAudioPlayer({
				contentUri: selectedAudioFile.contentUri,
				playOpts: new PlaySoundOpts({
					loop: true,
					panningType: SoundPanningType.spatial,
					volume: finalVolume,
					// disable additional effect-applications; we've already applied these to the finalVolume value
					effect_applyGlobalMultiplier: false,
					effect_applyLoudnessCurve: false,
				}).Finalized(),
				spatial: {
					initialDirection_clockwise: pool.cs_effect?.soundFile?.spatial_initialDirection_clockwise ?? true,
					switchDirInterval_min: pool.cs_effect?.soundFile?.spatial_switchDirInterval_min ?? 3,
					switchDirInterval_max: pool.cs_effect?.soundFile?.spatial_switchDirInterval_max ?? 12,
					revolutionDuration: pool.cs_effect?.soundFile?.spatial_revolutionDuration ?? 10,
					distance: pool.cs_effect?.soundFile?.spatial_distance ?? 0.3,
				},
				enableRecording: true,
			});
			webAudioPlayerRef.current = player;
			await player.Start();

			setStatusMessage("Starting recording...");
			await player.StartRecording();

			setStatusMessage(`Recording for ${bakeDuration} seconds...`);
			await new Promise(resolve=>setTimeout(resolve, bakeDuration * 1000));

			setStatusMessage("Stopping recording...");
			await player.StopRecording();

			setStatusMessage("Retrieving audio data...");
			const audioData = await player.GetRecordedAudio();

			setStatusMessage("Saving to file...");
			if (InAndroid(0)) {
				const filePath = GetFilePathForBakedSpatialAudio(pool.name, bakeDuration, finalVolume);
				await SaveAudioDataToFile(audioData, filePath);
				setStatusMessage(`Baked successfully to: ${filePath}`);
				console.log(`Baked spatial audio to: ${filePath}`);
			} else {
				const blob = new Blob(player.recordedChunks, {type: "audio/webm;codecs=opus"});
				const url = URL.createObjectURL(blob);
				const a = document.createElement("a");
				a.href = url;
				a.download = "SpatialAudio_Recording.webm";
				a.click();
				setStatusMessage(`Baked successfully to downloaded file.`);
				console.log(`Baked spatial audio to downloaded file.`);
			}

			setTimeout(()=>setStatusMessage(""), 5000);
			player.StopAndCleanup();
			webAudioPlayerRef.current = null;
		} catch (error) {
			const errorMsg = `Failed to bake spatial audio: ${ErrToStr(error)}`;
			console.error(errorMsg, error);
			setStatusMessage(errorMsg);
			webAudioPlayerRef.current?.StopAndCleanup();
			webAudioPlayerRef.current = null;
		} finally {
			setBaking(false);
		}
	};

	const StopBaking = ()=>{
		webAudioPlayerRef.current?.StopAndCleanup();
		webAudioPlayerRef.current = null;
		setBaking(false);
		setStatusMessage("Cancelled");
		setTimeout(()=>setStatusMessage(""), 3000);
	};

	return (
		<>
			<Button text={baking ? "Stop bake" : "Start bake"} onClick={async()=>{
				if (baking) {
					StopBaking();
				} else {
					await StartBaking();
				}
			}}/>
			{statusMessage && <Text ml={5}>{statusMessage}</Text>}
		</>
	);
});